/** Menu Builder copy — aligned with app/constants/menuBuilderCopy.js (menus + shopping list). */

/** Why to create a menu — not “organise recipes”. */
export const menuBuilderMenusWhyCopy = {
  subtitle:
    'See average and theoretical GP on a service — and get allergen grids ready for the floor.',
  tooltip:
    'Group costed dishes into lunch, dinner, or kids. You’ll see average GP, a theoretical GP from your sales mix, and allergen grids for that service.',
  emptyTitle: 'No menus yet',
  emptyBody:
    'Put costed dishes on a menu to see average GP, theoretical GP from your mix, and allergen grids for service.',
}

/** Why the allergen grid exists — not “select a scope”. */
export const menuBuilderAllergenGridWhyCopy = {
  subtitle:
    'Show FOH what each dish contains — so allergen questions don’t become a guess on a busy night.',
  tooltip:
    'Pick a menu to print or QR a grid. Guests get a clear yes/no, and you are not relying on whoever is on the pass.',
}

export const menuBuilderMenusCopy = {
  menuHeaderAvgGpLabel: 'Avg: {gp}% GP',
  menuCardWeightedGpLabel: 'Weighted {gp}% GP',
  menuCardEstGpLabel: '~£{amount} est. GP/mo',
  salesMixWeightedGpHelp:
    'Weighted GP reflects your expected monthly sales — higher-volume dishes count more.',
  salesMixTab: 'Sales mix',
  salesMixExplainer:
    'Enter how many portions you expect to sell per month for each recipe. We use this to estimate weighted GP% and total gross profit.',
  salesMixPeriodNote: 'Quantities are per month.',
  salesMixHelpTitle: 'How these GP numbers work',
  salesMixAvgGpHelp:
    'Average GP is a simple mean across all priced recipes on this menu.',
  salesMixEmptyQtyHint:
    'Enter monthly quantities above zero to see weighted GP and estimated gross profit.',
  salesMixMissingPriceHint:
    'Add selling price and cost on the recipe to include it in GP estimates.',
  salesMixRecipeGpLabel: '{gp}% GP',
  salesMixWeightedGpLabel: 'Weighted GP',
  salesMixTotalGpLabel: 'Est. total GP / month',
  salesMixQtyLabel: 'Expected sales / month',
  salesMixQtyPlaceholder: '0',
  salesMixNoRecipes:
    'Add recipes to this menu in Sections & recipes before setting a sales mix.',
  salesMixSaveCta: 'Save sales mix',
  salesMixSavingCta: 'Saving sales mix…',
  salesMixInvalidQtyTitle: 'Invalid quantity',
  salesMixInvalidQtyBody: 'Enter zero or a positive number for each recipe.',
  errorGeneric: 'Something went wrong. Please try again.',
}

export const menuBuilderShoppingListCopy = {
  guidanceBannerTitle: 'Shopping-list products only',
  guidanceBannerBody:
    'Choose ingredients from your shopping list — not the full supplier catalogue. Missing something? Add products from the Catalogues tab first, then return here.',
  guidanceBannerCta: 'Open Catalogues',
  emptyTitle: 'No shopping-list products yet',
  emptyBody:
    'Recipes use products from your shopping list — so you only cost ingredients you actually order through OMS.',
  catalogueHintTitle: 'Can’t find a product?',
  catalogueHintBody:
    'It is probably still in your supplier’s catalogue. Add it to your shopping list from the Catalogues tab, then return here.',
  catalogueHintSteps: [
    'Open the Catalogues tab for this venue',
    'Choose your supplier and browse their catalogue',
    'Add the product to your shopping list',
    'Return to this recipe to search again',
  ],
  dashboardBannerLead: 'Recipes are built from shopping list products in OMS.',
  dashboardBannerBeforeCataloguesLink:
    'Ingredients come from your shopping list — not the full supplier catalogue. Add products from the ',
  dashboardBannerBetweenLinks: ' tab, or connect more suppliers in ',
  dashboardBannerCataloguesCta: 'Open Catalogues',
}

export const menuBuilderMissingProductsCopy = {
  title: 'Some ingredients need attention',
  bodyTemplate:
    '{count} ingredient(s) are missing from your shopping list or have incomplete data.',
  editBlockedBody:
    'Fix or remove these before saving — live costing needs products you actually order.',
  listTitle: 'Affected ingredients:',
  addCta: 'Add to shopping list',
  addSuccessTitle: 'Products added',
  addSuccessBodyTemplate: 'Added {count} product(s) to your shopping list.',
  addFailedTitle: 'Could not add products',
  addFailedBody:
    'An error occurred while adding products to your shopping list. Please try again.',
  listBadgeLabel: 'Needs attention',
  listBadgeTooltip:
    'Some ingredients are missing from your shopping list or have incomplete data. View details to see more.',
}

export const menuBuilderSharedMissingIngredientsCopy = {
  listBadgeLabel: 'Needs Ingredient Review',
  listBadgeTooltip:
    'This recipe was shared with missing ingredients. Some ingredients could not be matched in the destination venue.',
}

export const menuBuilderPinCopy = {
  pinAccessibilityLabel: 'Favourite recipe',
  unpinAccessibilityLabel: 'Remove from favourites',
  errorMessage: 'Failed to update favourite. Please try again.',
}

export const menuBuilderGpReviewCopy = {
  changedBadge: 'GP changed',
  needsReviewHint: 'Margin may have shifted — worth a quick check',
  markReviewedCta: 'Mark reviewed',
  markReviewedAltCta: 'GP looks fine',
  markReviewedTooltip:
    "You've checked the updated margin. We'll flag again if GP changes later.",
  gpChangedBannerPrefix: 'GP changed',
  gpChangedBannerTemplate: 'GP changed: {delta}',
  noPricesYetBanner: 'No prices yet',
  costChangedBannerPrefix: 'Cost changed',
  costChangedBannerTemplate: 'Cost changed: {delta}',
  staleEconomicsTitle: 'Recipe cost may be out of date',
  staleEconomicsDescription:
    'Ingredient lines currently sum to £{lineSum}, but the saved recipe cost is £{headerCost}. Costs refresh automatically when you open Menu Builder — if this message stays, open the recipe to review details.',
  staleEconomicsListTitle: 'Some recipe costs are still catching up',
  staleEconomicsListSingular:
    '1 recipe may still have an outdated saved cost. Costs refresh automatically in the background — open it if this badge remains.',
  staleEconomicsListPlural:
    '{count} recipes may still have outdated saved costs. Costs refresh automatically in the background — open any flagged recipe if badges remain.',
  staleEconomicsListBadgeLabel: 'Cost mismatch',
  markReviewedErrorMessage:
    'Failed to mark recipe as reviewed. Please try again.',
}

export const menuBuilderListCopy = {
  menuHealthLabel: 'Average GP%',
  menuHealthEmptyHint:
    'Cost recipes to see your average GP% across costed dishes',
  limitReachedShort: 'Limit reached',
}

export default menuBuilderMenusCopy
